import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiBan, apiLogout, apiWhitelist } from "@/api/client.js";
import { Separator } from "@/components/ui/separator.js";
import { BanIpDialog } from "@/features/ban/BanIpDialog.js";
import { bouncersQueryKey } from "@/features/decisions/useBouncersQuery.js";
import { decisionsQueryKey } from "@/features/decisions/useDecisionsQuery.js";
import { IPFilterProvider } from "@/features/ip-filter/IPFilterContext.js";
import { WhitelistDialog } from "@/features/whitelist/WhitelistDialog.js";
import { SidebarNav } from "./SidebarNav.js";
import { TopBar } from "./TopBar.js";

export type AppShellOutletContext = {
  requestWhitelist: (ip?: string) => void;
  requestBan: (ip?: string) => void;
};

export function AppShell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [whitelistOpen, setWhitelistOpen] = useState(false);
  const [wlInitialIp, setWlInitialIp] = useState("");
  const [banOpen, setBanOpen] = useState(false);
  const [banInitialIp, setBanInitialIp] = useState("");

  const requestWhitelist = useCallback((ip = "") => {
    setWlInitialIp(ip);
    setWhitelistOpen(true);
  }, []);

  const requestBan = useCallback((ip = "") => {
    setBanInitialIp(ip);
    setBanOpen(true);
  }, []);

  const whitelistMut = useMutation({
    mutationFn: apiWhitelist,
    onSuccess: async () => {
      toast.success("Whitelist applied");
      await qc.invalidateQueries({ queryKey: decisionsQueryKey });
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  const banMut = useMutation({
    mutationFn: apiBan,
    onSuccess: async () => {
      toast.success("Ban applied");
      await qc.invalidateQueries({ queryKey: decisionsQueryKey });
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  async function logout() {
    await apiLogout();
    qc.clear();
    navigate("/login", { replace: true });
  }

  const outletCtx = useMemo<AppShellOutletContext>(
    () => ({
      requestWhitelist,
      requestBan,
    }),
    [requestWhitelist, requestBan]
  );

  return (
    <IPFilterProvider>
      <div className="grid min-h-screen md:grid-cols-[240px_1fr]">
        <aside className="hidden border-r bg-card md:flex md:flex-col">
          <div className="flex h-14 items-center border-b px-4">
            <span className="text-lg font-semibold tracking-tight">Menu</span>
          </div>
          <SidebarNav />
          <Separator className="mt-auto" />
          <p className="p-4 text-xs text-muted-foreground">CrowdSec bans via LAPI</p>
        </aside>

        <div className="flex min-h-screen min-w-0 flex-col">
          <TopBar
            onRefresh={() => {
              void qc.invalidateQueries({ queryKey: decisionsQueryKey });
              void qc.invalidateQueries({ queryKey: bouncersQueryKey });
            }}
            onLogout={() => void logout()}
            onOpenWhitelist={() => requestWhitelist("")}
            onOpenBan={() => requestBan("")}
          />
          <main className="flex-1 p-4 md:p-6">
            <Outlet context={outletCtx} />
          </main>
        </div>
      </div>

      <WhitelistDialog
        open={whitelistOpen}
        onOpenChange={setWhitelistOpen}
        initialIp={wlInitialIp}
        onSubmit={async (body: { ip: string; duration: string; reason: string }) => {
          await whitelistMut.mutateAsync(body);
        }}
      />
      <BanIpDialog
        open={banOpen}
        onOpenChange={setBanOpen}
        initialIp={banInitialIp}
        onSubmit={async (body: { ip: string; duration: string; reason: string }) => {
          await banMut.mutateAsync(body);
        }}
      />
    </IPFilterProvider>
  );
}
